if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      this.loadGeneration = (this.loadGeneration || 0) + 1;
      this.loadController?.abort();
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      if (this.modalContent) this.modalContent.innerHTML = '';
      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    safeProductPath(opener) {
      try {
        const url = new URL(opener?.getAttribute('data-product-url') || '', window.location.origin);
        if (url.origin !== window.location.origin || url.username || url.password) return null;
        return /^(?:\/[a-z]{2}(?:-[A-Z]{2})?)?\/products\/[a-z0-9][a-z0-9-]*\/?$/.test(url.pathname) ? url.pathname : null;
      } catch (_) { return null; }
    }

    reportLoadFailure(status, duration) {
      try {
        const recorded = window.BizDecksErrorMonitor?.capture?.({
          error_code: 'PRODUCT_SELECTOR_FAILED', interaction: 'select_product',
          http_status: Number.isInteger(status) ? status : undefined,
          duration_ms: Math.min(600000, Math.max(0, duration))
        });
        recorded?.catch?.(() => {});
      } catch (_) { /* Optional monitoring cannot block the product-page fallback. */ }
    }

    show(opener) {
      if (this.loadPromise) return this.loadPromise;
      const productPath = this.safeProductPath(opener);
      const startedAt = Date.now();
      const generation = this.loadGeneration = (this.loadGeneration || 0) + 1;
      const spinner = opener?.querySelector('.loading-overlay__spinner');
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.loadController = controller;
      opener?.setAttribute('aria-disabled', 'true');
      opener?.classList.add('loading');
      spinner?.classList.remove('hidden');
      this.loadPromise = Promise.resolve().then(async () => {
        let deadline, status;
        try {
          if (!productPath || !this.modalContent) throw new Error('QUICK_ADD_UNAVAILABLE');
          const timedOut = new Promise((_, reject) => {
            deadline = setTimeout(() => {
              controller?.abort();
              const error = new Error('QUICK_ADD_TIMEOUT');
              error.name = 'TimeoutError';
              reject(error);
            }, 5000);
          });
          const load = (async () => {
            const response = await fetch(productPath, {credentials:'same-origin',signal:controller?.signal});
            status = response.status;
            if (!response.ok) throw new Error('QUICK_ADD_HTTP');
            return response.text();
          })();
          const cancelled = controller ? new Promise((_, reject) => {
            controller.signal.addEventListener('abort', () => reject(new Error('QUICK_ADD_CANCELLED')), {once:true});
          }) : new Promise(() => {});
          const responseText = await Promise.race([load, timedOut, cancelled]);
          if (generation !== this.loadGeneration) return;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('[id^="MainProduct-"]');
          // Custom product templates do not contain the native quick-add section.
          if (!this.productElement?.parentElement) throw new Error('QUICK_ADD_SHAPE');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.productElement.setAttribute('data-update-url', 'false');
          this.productElement.dataset.isQuickView = true;
          this.setInnerHTML(this.modalContent, this.productElement.parentElement.innerHTML);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          if (generation === this.loadGeneration) super.show(opener);
        } catch (_) {
          if (generation !== this.loadGeneration) return;
          this.reportLoadFailure(status, Date.now() - startedAt);
          window.location.assign(productPath || '/collections/all');
        } finally {
          clearTimeout(deadline);
          if (this.loadController === controller) this.loadController = null;
          opener?.removeAttribute('aria-disabled');
          opener?.classList.remove('loading');
          spinner?.classList.add('hidden');
          this.loadPromise = null;
        }
      });
      return this.loadPromise;
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects,variant-tab-radios,variant-tab-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      // const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      // if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, variant-tab-radios, variant-tab-selects, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 768px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
